import PropTypes from 'prop-types';
import { useAuth } from 'hook/useAuth';
import { useAuth as oidcAuth } from 'react-oidc-context';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useQueryData } from 'hook/useQueryData';
import {
  useGetCurrentApplicationQuery,
  useGetOpenIDConfigurationQuery
} from 'services/applicationApi';
import SignIn from 'pages/authentication/sign-in';
import { getUrlParameter } from 'utils/urlUtils';
import app from 'config/app.json';
import { ROLE_OPERATION_READ } from 'utils/userUtils';
import loadable from '@loadable/component';
import Home from 'pages/template/Home';
import React from 'react';
import Error from 'pages/error';
import { PortalTemplate } from './pages/template/PortalTemplate';
import { StandaloneTemplate } from './pages/template/StandaloneTemplate';

const LoadableFolderDetails = loadable(() => import('pages/entities/details/FolderDetails'));

AuthorizationChecker.propTypes = {
  roleFunctionality: PropTypes.string,
  roleOperation: PropTypes.string,
  children: PropTypes.node
};

AuthorizationChecker.defaultProps = {};

/**
 * AuthorizationChecker component checks if a user has the required roles and functionalities.
 * If not, it redirects them either to the signIn page or the home page.
 * Otherwise, it renders the children components.
 *
 * @component
 * @prop {ReactNode} children - The children components to render.
 * @prop {string} roleFunctionality - The required role functionality for accessing a resource.
 * @prop {string} [roleOperation] - The required role operation for accessing a resource.
 */
function AuthorizationChecker({ children, roleFunctionality, roleOperation }) {
  const { currentUser } = useAuth();
  const authOIDC = oidcAuth();
  const location = useLocation();

  const { data: currentApplication } = useQueryData(useGetCurrentApplicationQuery);
  const { data: openIDConf } = useGetOpenIDConfigurationQuery();

  if (currentApplication?.secured && !currentUser.user) {
    // If OIDC mode redirect user to the Authority
    if (openIDConf?.enabled) {
      authOIDC?.signinRedirect();
      return null;
    }
    // Redirect them to the /signIn page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they signIn, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/signIn" state={{ from: location }} replace />;
  }

  const isUserUnauthorized =
    currentUser &&
    roleFunctionality &&
    roleOperation &&
    !currentUser.isAuthorized(roleFunctionality, roleOperation);
  if (isUserUnauthorized) {
    return (
      <Navigate
        to="/error"
        state={{
          messageId: 'error.message.insufficient.right',
          titleId: 'error.title.insufficient.right',
          code: 403
        }}
      />
    );
  }

  return children;
}

/**
 * ArcadeRoutes component is responsible for setting up and rendering the routes for the application.
 * It sets up both the portal routes and the standalone routes, using the passed routes configuration.
 *
 * @component
 * @prop {Object[]} routesConf - The configuration for the routes. Each object should have the path and other related info.
 */
const ArcadeRoutes = React.memo(({ routes }) => {
  const currentAppCode = getUrlParameter(window.location, app.url.paramApp)?.toUpperCase();
  const applicationRole = currentAppCode ? `APPLICATION_${currentAppCode}` : null;
  const portalReactRoutes = buildRoutes(routes, applicationRole, ROLE_OPERATION_READ);
  const standaloneRoutes = routes.filter((x) => x.standalone);
  const standaloneReactRoutes = buildRoutes(standaloneRoutes, applicationRole, ROLE_OPERATION_READ);

  let indexPath = 'index';
  if (routes?.length > 0) {
    const firstRoute = routes.at(0);
    indexPath = `/portal/${firstRoute.path.replace(':view', firstRoute.view)}`;
  }
  return (
    <Routes>
      <Route path="/portal" element={<PortalTemplate />}>
        {portalReactRoutes}
        <Route index path="*" element={<Home />} />
        <Route
          path="timeline/:view/details/:entityId"
          element={
            <AuthorizationChecker
              roleFunctionality={applicationRole}
              roleOperation={ROLE_OPERATION_READ}
            >
              <LoadableFolderDetails />
            </AuthorizationChecker>
          }
        />
        <Route
          path="advancedListing/:view/details/:entityId"
          element={
            <AuthorizationChecker
              roleFunctionality={applicationRole}
              roleOperation={ROLE_OPERATION_READ}
            >
              <LoadableFolderDetails />
            </AuthorizationChecker>
          }
        />
      </Route>
      <Route
        path="/standalone"
        element={<StandaloneTemplate routeConfigurations={standaloneRoutes} />}
      >
        {standaloneReactRoutes}
      </Route>
      <Route path="signIn" element={<SignIn />} />
      <Route path="error" element={<Error />} />
      <Route path="*" element={<Navigate to={indexPath} />} />
    </Routes>
  );

  /**
   * buildRoutes function is a recursive utility that maps the route configurations to Route components.
   *
   * @function
   * @param {Object[]} allRoutes - The list of route configurations.
   * @param {string} applicationRole - The application role for authorization checking.
   * @param {string} roleOperation - The role operation for authorization checking.
   * @returns {ReactNode[]} - An array of Route components.
   */
  function buildRoutes(allRoutes, applicationRole, roleOperation) {
    return allRoutes.flatMap((route) => {
      if (route.collapse) {
        return buildRoutes(route.collapse, applicationRole, roleOperation);
      }

      if (route.path) {
        return (
          <Route
            key={route.key}
            path={route.path}
            element={
              <AuthorizationChecker
                roleFunctionality={applicationRole}
                roleOperation={roleOperation}
              >
                {route.component}
              </AuthorizationChecker>
            }
          />
        );
      }

      return [];
    });
  }
});

ArcadeRoutes.propTypes = {
  routes: PropTypes.array.isRequired
};

export default ArcadeRoutes;
